import { useState, useCallback } from "react";

type StoredCounter = { value: number; updatedAt: number | null };

/**
 * Reads a counter written by usePersistentCounter, discarding it when it is
 * older than expiresInMs. Values written before the expiry option existed
 * carry no timestamp and count as expired.
 */
export function readPersistedCounter(
  key: string,
  initialValue: number = 0,
  expiresInMs?: number,
): number {
  try {
    const stored = parseStoredCounter(sessionStorage.getItem(key));

    if (stored === null) return initialValue;
    if (hasExpired(stored, expiresInMs)) {
      sessionStorage.removeItem(key);

      return initialValue;
    }

    return stored.value;
  } catch (error) {
    console.warn(`Failed to read from sessionStorage for key "${key}":`, error);

    return initialValue;
  }
}

function parseStoredCounter(stored: string | null): StoredCounter | null {
  if (stored === null) return null;
  if (stored.startsWith("{")) {
    const parsed = JSON.parse(stored) as Partial<StoredCounter>;

    if (typeof parsed.value !== "number" || Number.isNaN(parsed.value)) return null;

    return {
      value: parsed.value,
      updatedAt: typeof parsed.updatedAt === "number" ? parsed.updatedAt : null,
    };
  }

  const legacyValue = Number(stored);

  if (stored.trim() === "" || !Number.isFinite(legacyValue)) return null;

  return { value: legacyValue, updatedAt: null };
}

function hasExpired(stored: StoredCounter, expiresInMs?: number): boolean {
  if (expiresInMs === undefined) return false;
  if (stored.updatedAt === null) return true;

  return Date.now() - stored.updatedAt > expiresInMs;
}

/**
 * Applies a new value to a persisted counter and returns what was stored. A
 * functional update reads its previous value from storage, so an expired
 * counter starts a fresh cycle instead of building on a stale one.
 */
export function updatePersistedCounter(
  key: string,
  newValue: number | ((previous: number) => number),
  initialValue: number = 0,
  expiresInMs?: number,
): number {
  const previous = readPersistedCounter(key, initialValue, expiresInMs);
  const result = typeof newValue === "function" ? newValue(previous) : newValue;

  try {
    sessionStorage.setItem(key, JSON.stringify({ value: result, updatedAt: Date.now() }));
  } catch (error) {
    console.warn(`Failed to write to sessionStorage for key "${key}":`, error);
  }

  return result;
}

/**
 * Custom hook for managing a counter that persists across component remounts
 * using sessionStorage (resets when browser tab is closed)
 *
 * @param key - Unique key for storing the counter value in sessionStorage
 * @param initialValue - Initial value for the counter (default: 0)
 * @param expiresInMs - When set, a value stored longer ago than this is
 * discarded, both when reading it on mount and when updating it
 * @returns [value, setValue, reset] - Current value, setter function, and reset function
 */
export function usePersistentCounter(
  key: string,
  initialValue: number = 0,
  expiresInMs?: number,
) {
  const [value, setValue] = useState(() =>
    readPersistedCounter(key, initialValue, expiresInMs),
  );

  const setPersistentValue = useCallback(
    (newValue: number | ((previous: number) => number)) => {
      setValue(updatePersistedCounter(key, newValue, initialValue, expiresInMs));
    },
    [key, initialValue, expiresInMs],
  );

  const reset = useCallback(() => {
    setValue(initialValue);
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove from sessionStorage for key "${key}":`, error);
    }
  }, [key, initialValue]);

  return [value, setPersistentValue, reset] as const;
}
